import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

/**
 * Every server call goes through RTK Query, so caching, loading states and
 * invalidation live in one place. Endpoints are injected per feature
 * (store/endpoints/*), which keeps this file small and features independent.
 */
const readCsrf = (): string => document.cookie.match(/(?:^|;\s*)ac_csrf=([^;]+)/)?.[1] ?? '';

const raw = fetchBaseQuery({
  baseUrl: '/api',
  credentials: 'include',
  prepareHeaders: (headers) => {
    const csrf = readCsrf();
    if (csrf) headers.set('X-CSRF-Token', csrf);
    return headers;
  }
});

let refreshing: Promise<boolean> | null = null;

/** An expired access token is refreshed once, transparently, then the request is retried. */
const withRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extra) => {
  let result = await raw(args, api, extra);
  const url = typeof args === 'string' ? args : args.url;
  if (result.error?.status === 401 && !url.startsWith('/auth/')) {
    refreshing ??= Promise.resolve(raw({ url: '/auth/refresh', method: 'POST' }, api, extra))
      .then((r) => !r.error)
      .finally(() => {
        refreshing = null;
      });
    if (await refreshing) result = await raw(args, api, extra);
    else api.dispatch({ type: 'auth/sessionExpired' });
  }
  return result;
};

export const TAGS = ['Session', 'Dashboard', 'Agent', 'Contact', 'List', 'Campaign', 'Call', 'Message', 'Template', 'Number', 'Billing', 'Team', 'Audit', 'Settings', 'Dnc', 'AdminOrg', 'AdminNumber', 'Catalog', 'AdminSettings', 'AdminUser', 'Pipeline', 'Deal', 'Task', 'Timeline', 'CrmField', 'LeadSource', 'LeadEvent', 'Playbook', 'Approval', 'Automation', 'Booking', 'ErpSetup', 'Invoice', 'Report'] as const;

export const api = createApi({
  reducerPath: 'api',
  baseQuery: withRefresh,
  tagTypes: TAGS,
  endpoints: () => ({})
});

export interface CursorPage<T> {
  data: T[];
  nextCursor: string | null;
  total?: number;
}
